import * as React from 'react';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';
import useMeasure from 'react-use-measure';
import { useSwipeable } from 'react-swipeable';
import { useDebouncedCallback } from 'use-debounce';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { Sys } from 'src/types/contentful-sdk-compat';
import _clamp from 'lodash/clamp';
import {
  Box,
  Column,
  Container,
  Row,
  Section,
  Stack,
  Spacer,
} from 'src/components-v2/Layout';
import { ImageCards } from './ImageCards';
import { StatsCards } from './StatsCards';

import { getImageCardStyles, getStatCardStyles } from './cardStyles';
import { useDeviceSize } from 'src/hooks';

export interface IPaginatedCard {
  content: Array<React.ReactElement<unknown>>;
  sx: SxProps<ITheme>;
}

export const LayoutOptions = ['Paginated', 'Grid'] as const;

export type TLayout = (typeof LayoutOptions)[number];

export const AlignmentOptions = ['Left', 'Center'] as const;

export type TAlignment = (typeof AlignmentOptions)[number];

interface IPaginatorProps {
  cards: Array<IPaginatedCard> | Array<React.ReactElement<unknown>>;
  sx?: SxProps<ITheme>;
  sys?: Sys;
  heading?: React.ReactElement;
  appearance?: 'stat' | 'image';
  layout?: TLayout;
  alignment?: TAlignment;
}

const components = {
  stat: StatsCards,
  image: ImageCards,
};

const Paginator: React.FC<IPaginatorProps> = ({
  cards,
  sx,
  sys,
  heading,
  appearance = 'image',
  layout = 'Paginated',
  alignment = 'Left',
}) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });

  const isPaginated = layout === 'Paginated';
  const PaginatedCards = components[appearance];
  const cardStyles = {
    image: getImageCardStyles(cards),
    stat: getStatCardStyles(cards),
  };
  const initialState =
    cards?.map((c, i) => {
      return i === 0 ? true : false;
    }) || [];
  const [isActive, setIsActive] = React.useState(initialState);
  const [xPos, translateX] = React.useState(0);
  const [cardRef, cardRefBounds] = useMeasure();
  const [rowRef, rowRefBounds] = useMeasure();
  const device = useDeviceSize();
  const activeCard = isActive.findIndex((c) => {
    return c === true;
  });

  const getX = (index) => {
    const marginLeft =
      appearance === 'image' ? (rowRefBounds.width * 1) / 24 : 0;
    return (cardRefBounds.width + marginLeft) * index;
  };

  const handleClick = (index) => {
    if (!isPaginated) {
      return;
    }

    const x = getX(index);
    translateX(x);
    setIsActive((prevState) => {
      const newState = prevState.map((c, i) => {
        if (i === index) {
          return true;
        }
        return false;
      });
      return newState;
    });
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      handleClick(_clamp(activeCard + 1, 0, cards.length - 1));
    },
    onSwipedRight: () => {
      handleClick(_clamp(activeCard - 1, 0, cards.length - 1));
    },
    delta: 10, // min distance(px) before a swipe starts. *See Notes*
    preventScrollOnSwipe: true, // prevents scroll during swipe
    trackTouch: true, // track touch input
    trackMouse: true, // track mouse input
    swipeDuration: 1000, // allowable duration of a swipe (ms)
    touchEventOptions: { passive: true }, // options for touch listeners
  });

  const recalculateX = useDebouncedCallback((index) => {
    const x = getX(index);
    translateX(x);
  }, 100);

  React.useEffect(() => {
    if (!isPaginated) {
      return;
    }

    // reset the rows, this is uncommon, but will happen during testing and will scare people
    if (!device.isSmall && xPos !== 0) {
      translateX(0);
      setIsActive(initialState);
    }

    window.addEventListener('resize', recalculateX.bind(null, activeCard));
    return () => {
      window.removeEventListener('resize', recalculateX);
    };
  }, [
    recalculateX,
    device.isSmall,
    initialState,
    isActive,
    xPos,
    activeCard,
    layout,
    isPaginated,
  ]);

  return (
    <Section
      sx={{
        // Off-screen cards sit past the right edge on mobile, and body's
        // overflow-x: hidden doesn't stop phones from zooming out to them.
        overflowX: 'clip',
        ...sx,
      }}
      {...sx}
    >
      {heading && heading}
      <Container {...inspectorProps({ fieldId: 'internalTitle' })}>
        {/* we have a blank row just for measuring since merge refs is not working */}
        <Row ref={rowRef} />
        <Row
          component='ul'
          ref={handlers.ref}
          sx={{
            flexWrap: { xs: layout === 'Paginated' ? 'nowrap' : 'wrap' },
            transform: `translateX(-${xPos}px)`,
            transition: 'transform 0.3s ease-in-out',
            listStyle: 'none',
            ...cardStyles[appearance],
          }}
          rowSpacing={3.2}
          {...handlers.onMouseDown}
        >
          <PaginatedCards
            cards={cards}
            cardRef={cardRef as React.Ref<HTMLDivElement>}
            alignment={alignment}
          />
        </Row>
        {layout === 'Paginated' && (
          <>
            <Spacer sx={{ height: { xs: '15px', sm: '0' } }} />
            <Row sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <Column xs={24}>
                <Stack justifyContent='center' flexDirection='row' gap={0}>
                  {cards?.map((card, i) => {
                    return (
                      <Indicator
                        key={`indicator-${i}`}
                        isActive={isActive[i]}
                        index={i}
                        onClick={handleClick}
                      />
                    );
                  })}
                </Stack>
              </Column>
            </Row>
          </>
        )}
      </Container>
    </Section>
  );
};

const Indicator = ({ isActive = false, index, onClick, sx = {} }) => {
  return (
    <Box
      onClick={() => {
        onClick(index);
      }}
      key={`indicator-${index}`}
      component='button'
      aria-label='indicator'
      tabIndex={0}
      role='button'
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === 'Space') onClick(index);
      }}
    >
      <Box
        component='span'
        sx={{
          display: 'block',
          height: '14px',
          width: isActive ? '28px' : '14px',
          background: (theme) => {
            return isActive
              ? theme.palette['gradient/graphic/subtle/elizabeth']
              : theme.palette['gradient/background/vibrant/elizabeth'];
          },
          borderRadius: '8px',
          margin: isActive ? '2.0rem 0.8rem' : '2.0rem 0.8rem',
          ...sx,
        }}
      />
    </Box>
  );
};

export { Paginator, Indicator };
